import React, { useState } from "react";
import {
  BarChart3,
  Calendar,
  Download,
  Filter,
  Clock,
  Users,
  DollarSign,
  Trophy,
  FileText,
  Shield
} from "lucide-react";
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const Reports = ({ moduleName = "System", reportTypes = null, devices = [], assignments = [] }) => {
  // Default report types if none provided
  const defaultReportTypes = [
    "Daily Summary",
    "Weekly Report", 
    "Monthly Report",
    "Activity Log",
    "Performance Report"
  ];

  const moduleReportTypes = reportTypes || defaultReportTypes;

  const [reportConfig, setReportConfig] = useState({
    reportType: moduleReportTypes[0],
    period: "daily",
    startDate: new Date().toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0],
    customDateRange: false
  });

  const [loading, setLoading] = useState(false);

  const moduleReportTypesArray = moduleReportTypes;

  const periodOptions = [
    { value: "daily", label: "Daily" },
    { value: "monthly", label: "Monthly" },
    { value: "quarterly", label: "Quarterly" },
    { value: "half-yearly", label: "Half Yearly" },
    { value: "yearly", label: "Yearly" },
    { value: "custom", label: "Custom Date Range" }
  ];

  // Mock data for different report types
  const getMockData = (reportType) => {
    // Use actual biometric data if available
    if (moduleName === "Biometric Integration") {
      const biometricData = {
        "Device Status Report": devices.map(device => ({
          deviceId: device.id,
          deviceName: device.name,
          deviceType: device.type,
          apiEndpoint: device.endpoint,
          physicalLocation: device.location || 'N/A',
          status: device.status,
          totalAssignments: assignments.filter(a => a.device === device.name).length
        })),
        "Assignment Rules Report": assignments.map(assignment => ({
          assignmentId: assignment.id,
          deviceName: assignment.device,
          appliesTo: assignment.appliesTo,
          locationContext: assignment.location || 'N/A',
          accessSchedule: assignment.schedule
        })),
        "Biometric Activity Report": [
          ...devices.map(device => ({
            activityType: 'Device Configuration',
            entityName: device.name,
            action: 'Created/Updated',
            status: device.status,
            timestamp: new Date().toISOString().split('T')[0]
          })),
          ...assignments.map(assignment => ({
            activityType: 'Assignment Rule',
            entityName: assignment.device,
            action: 'Configured',
            status: 'Active',
            timestamp: new Date().toISOString().split('T')[0]
          }))
        ],
        "Device Performance Report": devices.map(device => ({
          deviceName: device.name,
          deviceType: device.type,
          status: device.status,
          uptime: device.status === 'ACTIVE' ? '99.5%' : device.status === 'MAINTENANCE' ? '0%' : '50%',
          totalScans: Math.floor(Math.random() * 1000) + 100,
          failedScans: Math.floor(Math.random() * 50),
          lastSync: new Date().toISOString().split('T')[0]
        })),
        "Integration Health Report": devices.map(device => ({
          deviceName: device.name,
          endpoint: device.endpoint,
          connectionStatus: device.status === 'ACTIVE' ? 'Connected' : 'Disconnected',
          lastPing: new Date().toISOString(),
          responseTime: device.status === 'ACTIVE' ? `${Math.floor(Math.random() * 100) + 50}ms` : 'N/A',
          errorCount: device.status === 'ACTIVE' ? 0 : Math.floor(Math.random() * 10)
        })),
        "Access Control Report": assignments.map(assignment => ({
          deviceName: assignment.device,
          userGroup: assignment.appliesTo,
          location: assignment.location || 'N/A',
          schedule: assignment.schedule,
          accessGranted: Math.floor(Math.random() * 500) + 100,
          accessDenied: Math.floor(Math.random() * 20)
        })),
        "Device Maintenance Report": devices.filter(d => d.status === 'MAINTENANCE').map(device => ({
          deviceName: device.name,
          deviceType: device.type,
          location: device.location || 'N/A',
          maintenanceStatus: 'In Progress',
          scheduledDate: new Date().toISOString().split('T')[0],
          estimatedCompletion: new Date(Date.now() + 86400000).toISOString().split('T')[0]
        })),
        "User Assignment Report": assignments.map(assignment => ({
          assignmentId: assignment.id,
          deviceName: assignment.device,
          userGroup: assignment.appliesTo,
          location: assignment.location || 'N/A',
          schedule: assignment.schedule,
          totalUsers: Math.floor(Math.random() * 100) + 10,
          activeUsers: Math.floor(Math.random() * 80) + 5
        }))
      };
      return biometricData[reportType] || [];
    }
    
    // Default mock data for other modules
    const baseData = {
      "AttendanceCorrection Requests": [
        { employeeId: "EMP-001", employeeName: "John Doe", requestDate: "2024-01-15", originalTime: "09:30", correctedTime: "09:00", reason: "Traffic delay", status: "Approved", approvedBy: "MGR001" },
        { employeeId: "EMP-002", employeeName: "Jane Smith", requestDate: "2024-01-14", originalTime: "18:00", correctedTime: "18:30", reason: "Client meeting", status: "Pending", approvedBy: "-" }
      ],
      "Leaves": [
        { employeeId: "EMP-001", employeeName: "John Doe", leaveType: "Casual Leave", startDate: "2024-01-20", endDate: "2024-01-22", days: 3, status: "Approved", appliedDate: "2024-01-15" },
        { employeeId: "EMP-003", employeeName: "Mike Johnson", leaveType: "Sick Leave", startDate: "2024-01-18", endDate: "2024-01-18", days: 1, status: "Approved", appliedDate: "2024-01-17" }
      ],
      "Timesheets": [
        { employeeId: "EMP-001", employeeName: "John Doe", date: "2024-01-15", checkIn: "09:00", checkOut: "18:00", totalHours: "9:00", overtime: "0:00", status: "Submitted" },
        { employeeId: "EMP-002", employeeName: "Jane Smith", date: "2024-01-15", checkIn: "09:15", checkOut: "18:30", totalHours: "9:15", overtime: "0:30", status: "Approved" }
      ],
      "Reimbursements": [
        { employeeId: "EMP-001", employeeName: "John Doe", category: "Travel", amount: 2500, requestDate: "2024-01-15", description: "Client visit - Mumbai", status: "Approved", approvedBy: "MGR001" },
        { employeeId: "EMP-002", employeeName: "Jane Smith", category: "Medical", amount: 1200, requestDate: "2024-01-14", description: "Health checkup", status: "Pending", approvedBy: "-" }
      ],
      "Bonus & Rewards": [
        { employeeId: "EMP-001", employeeName: "John Doe", type: "Performance Bonus", amount: 15000, quarter: "Q4 2023", awardedDate: "2024-01-10", reason: "Exceeded targets" },
        { employeeId: "EMP-003", employeeName: "Mike Johnson", type: "Spot Award", amount: 5000, quarter: "Q4 2023", awardedDate: "2024-01-12", reason: "Client appreciation" }
      ],
      "Appraisal": [
        { employeeId: "EMP-001", employeeName: "John Doe", period: "2023-24", currentRating: 4.2, previousRating: 3.8, increment: "12%", promotionEligible: "Yes", reviewDate: "2024-01-15" },
        { employeeId: "EMP-002", employeeName: "Jane Smith", period: "2023-24", currentRating: 3.9, previousRating: 3.7, increment: "8%", promotionEligible: "No", reviewDate: "2024-01-16" }
      ],
      "Promotion/Hike": [
        { employeeId: "EMP-001", employeeName: "John Doe", previousDesignation: "Senior Developer", newDesignation: "Lead Developer", previousSalary: 80000, newSalary: 95000, effectiveDate: "2024-02-01", approvedBy: "HR001" },
        { employeeId: "EMP-004", employeeName: "Sarah Wilson", previousDesignation: "Associate", newDesignation: "Senior Associate", previousSalary: 45000, newSalary: 52000, effectiveDate: "2024-02-01", approvedBy: "HR001" }
      ],
      "Policy Compliance": [
        { employeeId: "EMP-001", employeeName: "John Doe", policyName: "Data Security Policy", completionDate: "2024-01-10", status: "Completed", score: "95%", validUntil: "2024-12-31" },
        { employeeId: "EMP-002", employeeName: "Jane Smith", policyName: "Code of Conduct", completionDate: "2024-01-08", status: "Completed", score: "88%", validUntil: "2024-12-31" }
      ]
    };
    return baseData[reportType] || [];
  };

  const getReportHeaders = (reportType) => {
    // Biometric-specific headers
    if (moduleName === "Biometric Integration") {
      const biometricHeaders = {
        "Device Status Report": ["Device ID", "Device Name", "Device Type", "API Endpoint", "Physical Location", "Status", "Total Assignments"],
        "Assignment Rules Report": ["Assignment ID", "Device Name", "Applies To", "Location Context", "Access Schedule"],
        "Biometric Activity Report": ["Activity Type", "Entity Name", "Action", "Status", "Timestamp"],
        "Device Performance Report": ["Device Name", "Device Type", "Status", "Uptime", "Total Scans", "Failed Scans", "Last Sync"],
        "Integration Health Report": ["Device Name", "Endpoint", "Connection Status", "Last Ping", "Response Time", "Error Count"],
        "Access Control Report": ["Device Name", "User Group", "Location", "Schedule", "Access Granted", "Access Denied"],
        "Device Maintenance Report": ["Device Name", "Device Type", "Location", "Maintenance Status", "Scheduled Date", "Estimated Completion"],
        "User Assignment Report": ["Assignment ID", "Device Name", "User Group", "Location", "Schedule", "Total Users", "Active Users"]
      };
      return biometricHeaders[reportType] || [];
    }
    
    // Default headers for other modules
    const headers = {
      "AttendanceCorrection Requests": ["Employee ID", "Employee Name", "Request Date", "Original Time", "Corrected Time", "Reason", "Status", "Approved By"],
      "Leaves": ["Employee ID", "Employee Name", "Leave Type", "Start Date", "End Date", "Days", "Status", "Applied Date"],
      "Timesheets": ["Employee ID", "Employee Name", "Date", "Check In", "Check Out", "Total Hours", "Overtime", "Status"],
      "Reimbursements": ["Employee ID", "Employee Name", "Category", "Amount", "Request Date", "Description", "Status", "Approved By"],
      "Bonus & Rewards": ["Employee ID", "Employee Name", "Type", "Amount", "Quarter", "Awarded Date", "Reason"],
      "Appraisal": ["Employee ID", "Employee Name", "Period", "Current Rating", "Previous Rating", "Increment", "Promotion Eligible", "Review Date"],
      "Promotion/Hike": ["Employee ID", "Employee Name", "Previous Designation", "New Designation", "Previous Salary", "New Salary", "Effective Date", "Approved By"],
      "Policy Compliance": ["Employee ID", "Employee Name", "Policy Name", "Completion Date", "Status", "Score", "Valid Until"]
    };
    return headers[reportType] || [];
  };

  const getDateRange = () => {
    const today = new Date();
    let startDate = new Date();
    let endDate = new Date();

    switch (reportConfig.period) {
      case 'daily':
        startDate = endDate = today;
        break;
      case 'monthly':
        startDate = new Date(today.getFullYear(), today.getMonth(), 1);
        endDate = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        break;
      case 'quarterly':
        const quarter = Math.floor(today.getMonth() / 3);
        startDate = new Date(today.getFullYear(), quarter * 3, 1);
        endDate = new Date(today.getFullYear(), quarter * 3 + 3, 0);
        break;
      case 'half-yearly':
        const half = today.getMonth() < 6 ? 0 : 6;
        startDate = new Date(today.getFullYear(), half, 1);
        endDate = new Date(today.getFullYear(), half + 6, 0);
        break;
      case 'yearly':
        startDate = new Date(today.getFullYear(), 0, 1);
        endDate = new Date(today.getFullYear(), 11, 31);
        break;
      case 'custom':
        startDate = new Date(reportConfig.startDate);
        endDate = new Date(reportConfig.endDate);
        break;
      default:
        startDate = endDate = today;
    }

    return { startDate, endDate };
  };

  const downloadExcel = () => {
    setLoading(true);
    const data = getMockData(reportConfig.reportType);
    const headers = getReportHeaders(reportConfig.reportType);
    const { startDate, endDate } = getDateRange();
    
    const reportData = [
      [`${moduleName} - ${reportConfig.reportType} Report`],
      [`Generated on: ${new Date().toLocaleDateString('en-IN')}`],
      [`Period: ${reportConfig.period.charAt(0).toUpperCase() + reportConfig.period.slice(1)}`],
      [`Date Range: ${startDate.toLocaleDateString('en-IN')} to ${endDate.toLocaleDateString('en-IN')}`],
      [`Total Records: ${data.length}`],
      [],
      headers,
      ...data.map(item => headers.map(header => {
        const key = header.toLowerCase().replace(/\s+/g, '').replace(/[^a-z0-9]/g, '');
        const itemKey = Object.keys(item).find(k => 
          k.toLowerCase().replace(/\s+/g, '').replace(/[^a-z0-9]/g, '') === key
        );
        return item[itemKey] || '';
      }))
    ];
    
    const ws = XLSX.utils.aoa_to_sheet(reportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, reportConfig.reportType.substring(0, 31));
    XLSX.writeFile(wb, `${reportConfig.reportType.replace(/[^a-z0-9]/gi, '_')}_${reportConfig.period}_${new Date().toISOString().split('T')[0]}.xlsx`);
    
    setTimeout(() => setLoading(false), 1000);
  };

  const downloadPDF = () => {
    setLoading(true);
    const data = getMockData(reportConfig.reportType);
    const headers = getReportHeaders(reportConfig.reportType);
    const { startDate, endDate } = getDateRange();
    
    const doc = new jsPDF('l', 'mm', 'a4'); // Landscape orientation
    
    // Header
    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.text(`${moduleName} - ${reportConfig.reportType} Report`, 20, 20);
    
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.text(`Generated on: ${new Date().toLocaleDateString('en-IN')}`, 20, 30);
    doc.text(`Period: ${reportConfig.period.charAt(0).toUpperCase() + reportConfig.period.slice(1)}`, 20, 35);
    doc.text(`Date Range: ${startDate.toLocaleDateString('en-IN')} to ${endDate.toLocaleDateString('en-IN')}`, 20, 40);
    doc.text(`Total Records: ${data.length}`, 20, 45);

    const tableData = data.map(item => headers.map(header => {
      const key = header.toLowerCase().replace(/\s+/g, '').replace(/[^a-z0-9]/g, '');
      const itemKey = Object.keys(item).find(k => 
        k.toLowerCase().replace(/\s+/g, '').replace(/[^a-z0-9]/g, '') === key
      );
      return String(item[itemKey] || '');
    }));

    autoTable(doc, {
      head: [headers],
      body: tableData,
      startY: 55,
      styles: { 
        fontSize: 7,
        font: 'helvetica',
        cellPadding: 2
      },
      headStyles: {
        fillColor: [79, 70, 229],
        textColor: 255,
        fontStyle: 'bold'
      },
      columnStyles: {
        0: { cellWidth: 25 },
        1: { cellWidth: 35 }
      }
    });

    doc.save(`${reportConfig.reportType.replace(/[^a-z0-9]/gi, '_')}_${reportConfig.period}_${new Date().toISOString().split('T')[0]}.pdf`);
    
    setTimeout(() => setLoading(false), 1000);
  };

  const getReportIcon = (reportType) => {
    const icons = {
      "Daily Summary": BarChart3,
      "Weekly Report": Calendar,
      "Monthly Report": FileText,
      "Activity Log": Clock,
      "Performance Report": Trophy,
      "Employee Report": Users,
      "Financial Report": DollarSign,
      "Compliance Report": Shield,
      "Attendance Report": Clock,
      "Leave Report": Calendar,
      "Payroll Report": DollarSign,
      "Asset Report": FileText,
      "Training Report": Trophy
    };
    return icons[reportType] || BarChart3;
  };

  const ReportIcon = getReportIcon(reportConfig.reportType);

  return (
    <div className="bg-white rounded-lg shadow-sm border font-roboto">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <BarChart3 className="h-5 w-5 text-gray-500" />
            <h3 className="text-lg font-semibold text-gray-900 font-poppins">
              Reports - {moduleName}
            </h3>
          </div>
        </div>
      </div>

      {/* Report Configuration */}
      <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Report Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 font-poppins">
              Report Type
            </label>
            <div className="relative">
              <Filter className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <select
                value={reportConfig.reportType}
                onChange={(e) => setReportConfig({...reportConfig, reportType: e.target.value})}
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
              >
                {moduleReportTypesArray.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Period */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1 font-poppins">
              Period
            </label>
            <select
              value={reportConfig.period}
              onChange={(e) => setReportConfig({
                ...reportConfig, 
                period: e.target.value,
                customDateRange: e.target.value === 'custom'
              })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
            >
              {periodOptions.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>

          {/* Custom Date Range */}
          {reportConfig.period === 'custom' && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 font-poppins">
                  Start Date
                </label>
                <input
                  type="date"
                  value={reportConfig.startDate}
                  onChange={(e) => setReportConfig({...reportConfig, startDate: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 font-poppins">
                  End Date
                </label>
                <input
                  type="date"
                  value={reportConfig.endDate}
                  onChange={(e) => setReportConfig({...reportConfig, endDate: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
            </>
          )}
        </div>

        {/* Download Buttons */}
        <div className="flex space-x-3 mt-4">
          <button
            onClick={downloadExcel}
            disabled={loading}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50"
          >
            <Download className="h-4 w-4 mr-2" />
            {loading ? 'Generating...' : 'Download Excel'}
          </button>
          <button
            onClick={downloadPDF}
            disabled={loading}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50"
          >
            <Download className="h-4 w-4 mr-2" />
            {loading ? 'Generating...' : 'Download PDF'}
          </button>
        </div>
      </div>

      {/* Report Preview */}
      <div className="px-6 py-4">
        <div className="flex items-center space-x-2 mb-4">
          <ReportIcon className="h-5 w-5 text-indigo-600" />
          <h4 className="text-md font-semibold text-gray-900 font-poppins">
            {reportConfig.reportType} Preview
          </h4>
          <span className="text-sm text-gray-500">
            ({getMockData(reportConfig.reportType).length} records)
          </span>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {getReportHeaders(reportConfig.reportType).map((header, index) => (
                  <th key={index} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider font-poppins">
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {getMockData(reportConfig.reportType).slice(0, 5).map((row, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  {getReportHeaders(reportConfig.reportType).map((header, colIndex) => {
                    const key = header.toLowerCase().replace(/\s+/g, '').replace(/[^a-z0-9]/g, '');
                    const itemKey = Object.keys(row).find(k => 
                      k.toLowerCase().replace(/\s+/g, '').replace(/[^a-z0-9]/g, '') === key
                    );
                    return (
                      <td key={colIndex} className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {row[itemKey] || '-'}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
          {getMockData(reportConfig.reportType).length > 5 && (
            <div className="px-6 py-3 bg-gray-50 text-center text-sm text-gray-500">
              ... and {getMockData(reportConfig.reportType).length - 5} more records
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Reports;